/**
 * A font's glyph outlines as Godot's text server hands them to TextMesh: the glyph index of a
 * character from `cmap` (`sfntCmap.ts`), and the points of that glyph from `glyf` (`sfntGlyf.ts`) or
 * from `CFF ` and `CFF2` (`sfntCff.ts`). A WOFF or WOFF2 opens through `fontContainer.ts`, which
 * hands the readers the plain tables. A TrueType font that FreeType hints natively loads the bytecode
 * interpreter (`truetype/trueTypeHinting.ts`) on demand, which hints each `glyf` outline.
 *
 * Portions ported from Godot Engine (MIT).
 * Copyright (c) 2014-present Godot Engine contributors.
 * Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 * See THIRD-PARTY-NOTICES.md.
 */
import { cmapLookup } from './sfntCmap';
import { cffOutlineReader } from './sfntCff';
import { isNativelyHinted } from './glyphHinting';
import { glyphOutlineReader } from './sfntGlyf';
import { variedContours } from './glyphVariation';
import { openFontContainer } from './fontContainer';
import { OUTLINE_TABLE_TAGS } from './sfntTables';
/** `read`, or `fallback` when a corrupt table puts an offset past the buffer: a `DataView` read throws a `RangeError` there. */
function orOnRangeError(read, fallback) {
    try {
        return read();
    }
    catch (error) {
        if (!(error instanceof RangeError))
            throw error;
        return fallback;
    }
}
/**
 * Reads the outlines of a TrueType or OpenType-CFF font in a plain, WOFF or WOFF2 file, or returns
 * `null`, never throws, for a font with no `glyf` or CFF table, no readable `cmap` or a table that
 * does not fit its buffer. The reader answers no glyph, not an error, for an offset a corrupt table
 * puts past the buffer.
 */
export async function openFontOutlines(bytes) {
    const sfnt = await openFontContainer(bytes, OUTLINE_TABLE_TAGS);
    return sfnt && readSfntOutlines(sfnt, await glyphHintingOf(sfnt));
}
/** The TrueType hinting of a natively hinted `glyf` font, or null for a font FreeType does not hint so. */
async function glyphHintingOf(sfnt) {
    if (!sfnt.tables.has('glyf') || !isNativelyHinted(sfnt))
        return null;
    const { trueTypeHinting } = await import('./truetype/trueTypeHinting');
    return orOnRangeError(() => trueTypeHinting(sfnt), null);
}
function readSfntOutlines(sfnt, hinting) {
    const cmap = sfnt.tables.get('cmap');
    const outlineOf = glyphOutlineReader(sfnt, hinting) ?? cffOutlineReader(sfnt);
    if (!cmap || !outlineOf)
        return null;
    const lookup = orOnRangeError(() => cmapLookup(sfnt.dv, cmap.offset), null);
    if (!lookup)
        return null;
    return {
        glyphIndex: (codePoint) => orOnRangeError(() => lookup(codePoint), 0),
        glyphContours: (glyphIndex, fontSizePx, variation) => orOnRangeError(() => {
            const outline = outlineOf(glyphIndex, fontSizePx);
            return outline && variedContours(outline, fontSizePx, variation);
        }, null),
    };
}
//# sourceMappingURL=sfntOutlines.js.map